/**
 * Route-level loading skeleton for client portal pages.
 * Matches PageHeader + content area layout. Use when entire page is loading.
 */
import React from 'react';
import SkeletonBlock from './SkeletonBlock';
import SkeletonRoot from './SkeletonRoot';

export interface RouteSkeletonProps {
  /** Optional: 'default' (title left) or 'centered' to match PageHeader layout */
  layout?: 'default' | 'centered';
  /** Optional: show subtitle placeholder */
  showSubtitle?: boolean;
  /** Optional: content variant - 'single' (Documents/Profile) or 'wide' (Dashboard) */
  contentVariant?: 'single' | 'wide';
  className?: string;
  loadingLabel?: string;
}

const RouteSkeleton: React.FC<RouteSkeletonProps> = ({
  layout = 'default',
  showSubtitle = true,
  contentVariant = 'single',
  className = '',
  loadingLabel = 'Loading page',
}) => {
  const isCentered = layout === 'centered';
  const maxWidth = contentVariant === 'wide' ? 'max-w-7xl' : 'max-w-5xl';

  return (
    <SkeletonRoot loadingLabel={loadingLabel}>
      <div className={`min-h-screen bg-white ${className}`}>
        <div className={`mx-auto py-8 px-4 sm:px-6 lg:px-8 ${maxWidth}`}>
          <header
            className={`mb-6 ${isCentered ? 'text-center' : 'flex flex-col sm:flex-row sm:items-start sm:justify-between sm:gap-4'}`}
          >
            <div className={isCentered ? 'flex flex-col items-center' : 'min-w-0 flex-1'}>
              <SkeletonBlock className={`h-8 w-48 mx-auto sm:mx-0 ${isCentered ? '' : ''}`} />
              {showSubtitle && (
                <SkeletonBlock className="mt-2 h-4 w-72 mx-auto sm:mx-0" />
              )}
            </div>
          </header>

          <div className="space-y-6">
            <div className="rounded-2xl border border-primary-200/50 bg-white p-6">
              <div className="space-y-4">
                <SkeletonBlock className="h-4 w-3/4" />
                <SkeletonBlock className="h-16 w-full rounded-xl" />
                <SkeletonBlock className="h-16 w-full rounded-xl" />
                <SkeletonBlock className="h-16 w-full rounded-xl" />
                <SkeletonBlock className="h-4 w-1/2" />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="rounded-2xl border border-primary-200/50 bg-white p-6">
                <SkeletonBlock className="mb-4 h-5 w-32" />
                <div className="space-y-2">
                  <SkeletonBlock className="h-4 w-full" />
                  <SkeletonBlock className="h-4 w-4/5" />
                </div>
              </div>
              <div className="rounded-2xl border border-primary-200/50 bg-white p-6">
                <SkeletonBlock className="mb-4 h-5 w-32" />
                <div className="space-y-2">
                  <SkeletonBlock className="h-4 w-full" />
                  <SkeletonBlock className="h-4 w-3/4" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </SkeletonRoot>
  );
};

export default RouteSkeleton;
